export const DRYBULK_CARGO_OPS_BE_TOKEN = "drybulk-cargo-operations";

export const DRYBULK_CARGO_OPS_ACCESS_PERMISSION = "hasDrybulkCargoOperationsAccess";

export type SequenceOptionList = "cargo-codes" | "grades" | "stock-piles" | "shippers";

export const DRYBULK_CARGO_OPS_QUERY_KEYS = {
  all: ["drybulk-cargo-operations"] as const,
  list: () => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "list"] as const,
  searchAll: () => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "search"] as const,
  search: (criteria: unknown) => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "search", criteria] as const,
  detail: (id: string) => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "detail", id] as const,
  options: (list: SequenceOptionList) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "options", list] as const,
  equipmentOptions: (id: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "equipment-options", id] as const,
  operationNominations: (id: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "nominations", id] as const,
  toleranceChanges: (id: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "tolerance-changes", id] as const,
  delays: (id: string) => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "delays", id] as const,
  operationLog: (id: string, page: number, status: string | null | undefined) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "operation-log", id, page, status ?? null] as const,
  messages: (id: string, threadKey: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "messages", id, threadKey] as const,
  messageAttachments: (id: string, threadKey: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "message-attachments", id, threadKey] as const,
  delayTypes: () => [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "delay-types"] as const,
  delayCodes: (delayTypeCode: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "delay-codes", delayTypeCode] as const,
  finalLoadingReport: (id: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "final-loading-report", id] as const,
  finalDelayReport: (id: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "final-delay-report", id] as const,
  party: (id: string, type: string, code: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "party", id, type, code] as const,
  addOperationGate: (vesselCallId: string) =>
    [...DRYBULK_CARGO_OPS_QUERY_KEYS.all, "add-operation-gate", vesselCallId] as const,
};

export const DRYBULK_CARGO_OPS_ROUTE_SEGMENTS = {
  addOperation: "new",
  importDeferred: "new/import",
  sequence: "sequences",
} as const;

export const DRYBULK_CARGO_OPS_LANG_BASE = "drybulk-cargo-operations";

export const SEQUENCE_EDITABLE_STATUSES: readonly string[] = [
  "DRAFT",
  "PENDING_APPROVAL",
  "REJECTED",
];

export const DETAIL_REFETCH_INTERVAL_MS = 30_000;

export const GRID_REFETCH_INTERVAL_MS = 60_000;
export const OPERATION_LOG_PAGE_SIZE = 100;

export const OPERATION_STATUSES: readonly string[] = [
  "DRAFT",
  "PENDING_APPROVAL",
  "REJECTED",
  "PENDING_SHIPPER_READINESS",
  "SHIPPER_READY",
  "ONGOING",
  "WAITING_FINAL_DRAFT_SURVEY",
  "COMPLETE",
];

export const SUBMITTABLE_STATUS = "DRAFT";

export const SUBMIT_FROM_STATUSES: readonly string[] = ["DRAFT", "REJECTED"];

export const TOTAL_QUANTITY_EDITABLE_STATUSES: readonly string[] = SEQUENCE_EDITABLE_STATUSES;

export const DRYBULK_CARGO_OPS_TELEMETRY = {
  module: "drybulk_cargo_operations",
  sequencesScreen: "CargoOperationSequences",
  operationScreen: "CargoOperation",
  gridScreen: "CargoOperationsGrid",
  queueScreen: "OperationsQueue",
  addScreen: "AddCargoOperation",
  documentsScreen: "CargoOperationDocuments",
  delaysScreen: "CargoOperationDelays",
  operationLogScreen: "CargoOperationLog",
  messagesScreen: "CargoOperationMessages",
  finalLoadingReportScreen: "CargoOperationFinalLoadingReport",
  finalDelayReportScreen: "CargoOperationFinalDelayReport",
} as const;

export const MESSAGE_ENTITY_TYPE = "DRY_BULK_CARGO_OPERATION";
export const MESSAGE_SENT_SHIPPING_AGENT = "DRY_BULK_CARGO_OPERATION_MESSAGE_SENT_SHIPPING_AGENT";
export const MESSAGE_SENT_OPERATIONS = "DRY_BULK_CARGO_OPERATION_MESSAGE_SENT_OPERATIONS";
export const MESSAGE_DRAFTS_PERSIST_KEY = "drybulk-cargo-ops-message-drafts";
export const MESSAGE_MAX_FILES = 10;

export const PENDING_APPROVAL_STATUS = "PENDING_APPROVAL";
export const REVIEW_POLL_INTERVAL_MS = 2_000;
export const REVIEW_POLL_TIMEOUT_MS = 30_000;

export const EQUIPMENT_EDITABLE_OPERATION_STATUSES: readonly string[] = [
  "PENDING_SHIPPER_READINESS",
  "SHIPPER_READY",
  "ONGOING",
];
export const EQUIPMENT_EDITABLE_SEQUENCE_STATUSES: readonly string[] = [
  "PENDING_INFORMATION",
  "UPCOMING",
];

export const INCREMENT_WRITABLE_OPERATION_STATUSES: readonly string[] = [
  "SHIPPER_READY",
  "ONGOING",
];

export const INCREMENT_QUANTITY_PATTERN = /^\d{1,11}(\.\d)?$/;

export const WAITING_FINAL_DRAFT_SURVEY_STATUS = "WAITING_FINAL_DRAFT_SURVEY";
export const COMPLETE_STATUS = "COMPLETE";

export const TOLERANCE_EDITABLE_OPERATION_STATUSES: readonly string[] = [
  "PENDING_SHIPPER_READINESS",
  "SHIPPER_READY",
  "ONGOING",
  "WAITING_FINAL_DRAFT_SURVEY",
  "COMPLETE",
];

export const DELAY_WRITABLE_STATUSES: readonly string[] = ["ONGOING", "WAITING_FINAL_DRAFT_SURVEY"];

export const GENERAL_INFO_EDITABLE_STATUSES: readonly string[] = [
  "PENDING_SHIPPER_READINESS",
  "SHIPPER_READY",
  "ONGOING",
  "WAITING_FINAL_DRAFT_SURVEY",
  "COMPLETE",
];

export const DELAY_FORM_PERSIST_KEY = "drybulk-cargo-ops-delay-form";
export const GENERAL_INFO_FORM_PERSIST_KEY = "drybulk-cargo-ops-general-info-form";

export const FINAL_DRAFT_SURVEY_PATTERN = /^\d{1,11}(\.\d{1,3})?$/;
export const TOLERANCE_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;
export const HELD_INTERMEDIATE_SURVEYS_PERSIST_KEY = "drybulk-cargo-ops-intermediate-surveys";
export const FINAL_DRAFT_SURVEY_FORM_PERSIST_KEY = "drybulk-cargo-ops-final-draft-survey";

export const PLAN_ATTACHMENT_TYPES = ["LOADING_PLAN", "STOWAGE_PLAN"] as const;
export type PlanAttachmentType = (typeof PLAN_ATTACHMENT_TYPES)[number];

export const GENERAL_ATTACHMENT_TYPES = [
  "SHIPPING_INSTRUCTIONS",
  "MATES_RECEIPT",
  "DRAFT_BILL_OF_LADING",
  "CERTIFICATE_OF_ORIGIN",
  "QUALITY_CERTIFICATE",
  "QUANTITY_CERTIFICATE",
  "SURVEY_REPORT",
  "OTHER",
] as const;
